import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Box
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

function VendorNotificationPanel({ vendorId, preferences, onUpdatePreferences }) {
  const [localPreferences, setLocalPreferences] = useState(preferences || {});

  useEffect(() => {
    setLocalPreferences(preferences || {});
  }, [preferences]);

  const handleToggle = (key) => {
    const updated = {
      ...localPreferences,
      [key]: !localPreferences[key]
    };
    setLocalPreferences(updated);
    onUpdatePreferences(updated);
  };

  const notificationTypes = [
    {
      key: 'maintenance_requests',
      label: 'Maintenance Requests',
      description: 'Get notified when new maintenance requests are assigned'
    },
    {
      key: 'work_orders',
      label: 'Work Orders',
      description: 'Notifications for work order updates and completions'
    },
    {
      key: 'payment_reminders',
      label: 'Payment Reminders',
      description: 'Reminders for pending invoices and payments'
    },
    {
      key: 'emergency_requests',
      label: 'Emergency Requests',
      description: 'Immediate notifications for emergency situations'
    },
    {
      key: 'schedule_changes',
      label: 'Schedule Changes',
      description: 'Updates to scheduled appointments and visits'
    }
  ];

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <ArgonBox mb={2}>
        <ArgonTypography variant="h6" fontWeight="bold">
          Vendor Notification Preferences
        </ArgonTypography>
        <ArgonTypography variant="body2" color="text">
          Configure when and how you receive notifications for vendor-related activities
        </ArgonTypography>
      </ArgonBox>

      <Divider sx={{ mb: 2 }} />

      <List>
        {notificationTypes.map((type, index) => (
          <ListItem key={type.key} divider={index < notificationTypes.length - 1}>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    {type.label}
                  </Typography>
                  {type.key === 'emergency_requests' && (
                    <Chip 
                      label="Priority" 
                      size="small" 
                      color="error" 
                      variant="outlined" 
                    />
                  )}
                </Box>
              }
              secondary={type.description}
            />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPreferences[type.key] || false}
                    onChange={() => handleToggle(type.key)}
                    color="primary"
                  />
                }
                label=""
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <ArgonBox>
        <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1}>
          Notification Methods
        </ArgonTypography>
        <Box display="flex" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.email_notifications || false}
                onChange={() => handleToggle('email_notifications')}
                color="primary"
              />
            }
            label="Email"
          />
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.sms_notifications || false}
                onChange={() => handleToggle('sms_notifications')}
                color="primary"
              />
            }
            label="SMS"
          />
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.push_notifications || true}
                onChange={() => handleToggle('push_notifications')}
                color="primary"
              />
            }
            label="Push"
          />
        </Box>
      </ArgonBox>
    </Paper>
  );
}

VendorNotificationPanel.propTypes = {
  vendorId: PropTypes.string,
  preferences: PropTypes.object,
  onUpdatePreferences: PropTypes.func
};

VendorNotificationPanel.defaultProps = {
  vendorId: null,
  preferences: {},
  onUpdatePreferences: () => {}
};

export default VendorNotificationPanel;